/**
 * Canonical machines dataset for Fenix Srl.
 *
 * Each entry describes a single grinding machine: identity, category, imagery
 * and optional technical specs. Copy that changes between Italian and English
 * (name stays the same, short/long descriptions do) is referenced via
 * translation keys — see `machinesData.*` in `public/locales/{lang}/common.json`.
 *
 * Slugs are kebab-case and short (`fb-75`, `fa-60`, `favretto-me-200`): they
 * drive the URL segments under `/it/macchine/:slug` and `/en/machines/:slug`.
 */

export type MachineCategory = 'tangenziali' | 'doppio-montante'

export interface MachineSpec {
  /** Translation key under `machinesData.specs` */
  labelKey: string
  /** Literal value — already unitised, not translated */
  value: string
}

export interface Machine {
  /** URL slug — kebab-case, short (e.g. `fb-75`, `favretto-me-200`) */
  slug: string
  /** Display name — used identically in both languages */
  name: string
  /** Grouping used on the listing page */
  category: MachineCategory
  /** Primary image (absolute public path) */
  image: string
  /** Optional gallery images (absolute public paths) */
  gallery?: string[]
  /** i18n key yielding a short 1-line description (listing/card) */
  shortDescriptionKey: string
  /** i18n key yielding the long technical description (detail page). Value may be an array of paragraphs. */
  descriptionKey: string
  /** Optional technical specifications (label key + raw value) */
  specs?: MachineSpec[]
  /** Optional catalog PDF (absolute public path). `null` means no catalog available. */
  catalogPdf: string | null
  /** i18n key for an alt-tag describing the image. Falls back to name when absent. */
  imageAltKey?: string
}

const CATALOG_GENERAL_IT = '/documents/machines/catalogo-rettificatrici-fenix.pdf'
const CATALOG_DOUBLE_COLUMN = '/documents/machines/doppio-montante-testa-orizzontale.pdf'
const CATALOG_DOUBLE_COLUMN_ALT = '/documents/machines/doppio-montante-testa-alternata.pdf'
const CATALOG_DOUBLE_COLUMN_ROTARY = '/documents/machines/doppio-montante-tavola-rotante.pdf'
const CATALOG_ROTARY_TABLE = '/documents/machines/tavola-rotante-testa-orizzontale.pdf'

export const machines: Machine[] = [
  // ------------------------------------------------------------------
  // Tangential grinding machines — series FA (linear reciprocating table, small)
  // ------------------------------------------------------------------
  {
    slug: 'fa-60',
    name: 'Fenix FA 60',
    category: 'tangenziali',
    image: '/images/machines/fenix-fa-60.jpg',
    shortDescriptionKey: 'machinesData.items.fa60.short',
    descriptionKey: 'machinesData.items.fa60.description',
    imageAltKey: 'machinesData.items.fa60.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '600 × 400 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '400 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '5.5 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'fa-75',
    name: 'Fenix FA 75',
    category: 'tangenziali',
    image: '/images/machines/fenix-fa-75.jpg',
    shortDescriptionKey: 'machinesData.items.fa75.short',
    descriptionKey: 'machinesData.items.fa75.description',
    imageAltKey: 'machinesData.items.fa75.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '750 × 400 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '400 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '5.5 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  // ------------------------------------------------------------------
  // Series FB (linear reciprocating table, medium)
  // ------------------------------------------------------------------
  {
    slug: 'fb-75',
    name: 'Fenix FB 75',
    category: 'tangenziali',
    image: '/images/machines/fenix-fb-75.jpg',
    shortDescriptionKey: 'machinesData.items.fb75.short',
    descriptionKey: 'machinesData.items.fb75.description',
    imageAltKey: 'machinesData.items.fb75.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '750 × 500 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '500 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '7.5 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'fb-100',
    name: 'Fenix FB 100',
    category: 'tangenziali',
    image: '/images/machines/fenix-fb-100.jpg',
    shortDescriptionKey: 'machinesData.items.fb100.short',
    descriptionKey: 'machinesData.items.fb100.description',
    imageAltKey: 'machinesData.items.fb100.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '1000 × 500 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '500 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '7.5 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  // ------------------------------------------------------------------
  // Series FC (linear reciprocating table, heavy)
  // ------------------------------------------------------------------
  {
    slug: 'fc-100',
    name: 'Fenix FC 100',
    category: 'tangenziali',
    image: '/images/machines/fenix-fc-100.jpg',
    gallery: ['/images/machines/fenix-fc-100.jpg', '/images/machines/fenix-fc-100-alt.jpg'],
    shortDescriptionKey: 'machinesData.items.fc100.short',
    descriptionKey: 'machinesData.items.fc100.description',
    imageAltKey: 'machinesData.items.fc100.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '1000 × 600 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '600 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '11 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'fc-130',
    name: 'Fenix FC 130',
    category: 'tangenziali',
    image: '/images/machines/fenix-fc-130.jpg',
    shortDescriptionKey: 'machinesData.items.fc130.short',
    descriptionKey: 'machinesData.items.fc130.description',
    imageAltKey: 'machinesData.items.fc130.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '1300 × 600 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '600 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '11 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'fc-160',
    name: 'Fenix FC 160',
    category: 'tangenziali',
    image: '/images/machines/fenix-fc-160.jpg',
    shortDescriptionKey: 'machinesData.items.fc160.short',
    descriptionKey: 'machinesData.items.fc160.description',
    imageAltKey: 'machinesData.items.fc160.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '1600 × 600 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '600 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '15 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'fc-200',
    name: 'Fenix FC 200',
    category: 'tangenziali',
    image: '/images/machines/fenix-fc-200.jpg',
    shortDescriptionKey: 'machinesData.items.fc200.short',
    descriptionKey: 'machinesData.items.fc200.description',
    imageAltKey: 'machinesData.items.fc200.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '2000 × 700 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '700 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '15 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  // ------------------------------------------------------------------
  // Series FD (linear reciprocating table, XXL)
  // ------------------------------------------------------------------
  {
    slug: 'fd-150',
    name: 'Fenix FD 150',
    category: 'tangenziali',
    image: '/images/machines/fenix-fd-150.jpg',
    shortDescriptionKey: 'machinesData.items.fd150.short',
    descriptionKey: 'machinesData.items.fd150.description',
    imageAltKey: 'machinesData.items.fd150.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '1500 × 600 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '600 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '15 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'fd-300',
    name: 'Fenix FD 300',
    category: 'tangenziali',
    image: '/images/machines/fenix-fd-300.jpg',
    shortDescriptionKey: 'machinesData.items.fd300.short',
    descriptionKey: 'machinesData.items.fd300.description',
    imageAltKey: 'machinesData.items.fd300.alt',
    catalogPdf: CATALOG_GENERAL_IT,
    specs: [
      { labelKey: 'workingRangeX', value: '3000 × 800 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '800 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.linearReciprocating' },
      { labelKey: 'spindleMotor', value: '22 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  // ------------------------------------------------------------------
  // Series FR (rotary table)
  // ------------------------------------------------------------------
  {
    slug: 'fr-40',
    name: 'Fenix FR 40',
    category: 'tangenziali',
    image: '/images/machines/fenix-fr-40.jpg',
    shortDescriptionKey: 'machinesData.items.fr40.short',
    descriptionKey: 'machinesData.items.fr40.description',
    imageAltKey: 'machinesData.items.fr40.alt',
    catalogPdf: CATALOG_ROTARY_TABLE,
    specs: [
      { labelKey: 'tableDiameter', value: '400 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '300 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.rotaryTable' },
      { labelKey: 'spindleMotor', value: '5.5 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'fr-60',
    name: 'Fenix FR 60',
    category: 'tangenziali',
    image: '/images/machines/fenix-fr-60.jpg',
    shortDescriptionKey: 'machinesData.items.fr60.short',
    descriptionKey: 'machinesData.items.fr60.description',
    imageAltKey: 'machinesData.items.fr60.alt',
    catalogPdf: CATALOG_ROTARY_TABLE,
    specs: [
      { labelKey: 'tableDiameter', value: '600 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '400 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.rotaryTable' },
      { labelKey: 'spindleMotor', value: '7.5 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'fr-75',
    name: 'Fenix FR 75',
    category: 'tangenziali',
    image: '/images/machines/fenix-fr-75.jpg',
    shortDescriptionKey: 'machinesData.items.fr75.short',
    descriptionKey: 'machinesData.items.fr75.description',
    imageAltKey: 'machinesData.items.fr75.alt',
    catalogPdf: CATALOG_ROTARY_TABLE,
    specs: [
      { labelKey: 'tableDiameter', value: '750 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '500 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.rotaryTable' },
      { labelKey: 'spindleMotor', value: '11 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  // ------------------------------------------------------------------
  // Double-column grinding machines — series FF-U
  // ------------------------------------------------------------------
  {
    slug: 'ff-u-200',
    name: 'Fenix FF-U 200',
    category: 'doppio-montante',
    image: '/images/machines/fenix-ff-u-200.jpg',
    shortDescriptionKey: 'machinesData.items.ffU200.short',
    descriptionKey: 'machinesData.items.ffU200.description',
    imageAltKey: 'machinesData.items.ffU200.alt',
    catalogPdf: CATALOG_DOUBLE_COLUMN,
    specs: [
      { labelKey: 'workingRangeX', value: '2000 × 800 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '800 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.reciprocatingDoubleColumn' },
      { labelKey: 'spindleMotor', value: '22 kW' },
      { labelKey: 'cnc', value: 'Siemens Sinumerik' },
    ],
  },
  {
    slug: 'favretto-me-200',
    name: 'FAVRETTO ME 200',
    category: 'doppio-montante',
    image: '/images/machines/favretto-me-200.jpg',
    shortDescriptionKey: 'machinesData.items.favrettoMe200.short',
    descriptionKey: 'machinesData.items.favrettoMe200.description',
    imageAltKey: 'machinesData.items.favrettoMe200.alt',
    catalogPdf: CATALOG_DOUBLE_COLUMN_ALT,
    specs: [
      { labelKey: 'workingRangeX', value: '2000 × 1000 mm' },
      { labelKey: 'maxWorkpieceHeight', value: '900 mm' },
      { labelKey: 'tableType', value: 'machinesData.specValues.reciprocatingDoubleColumn' },
      { labelKey: 'spindleMotor', value: '30 kW' },
      { labelKey: 'condition', value: 'machinesData.specValues.refurbished' },
    ],
  },
]

/**
 * Lookup a machine by its URL slug. Returns `undefined` if no match.
 */
export function getMachineBySlug(slug: string): Machine | undefined {
  return machines.find((m) => m.slug === slug)
}

/**
 * Return the list of machines in a given category.
 */
export function getMachinesByCategory(category: MachineCategory): Machine[] {
  return machines.filter((m) => m.category === category)
}

/**
 * Given a machine, return up to `limit` related machines from the same
 * category, excluding the machine itself. Falls back to machines of the
 * other category if fewer than `limit` are available.
 */
export function getRelatedMachines(
  current: Machine,
  limit = 3
): Machine[] {
  const sameCategory = machines.filter(
    (m) => m.category === current.category && m.slug !== current.slug
  )
  if (sameCategory.length >= limit) return sameCategory.slice(0, limit)
  const other = machines.filter(
    (m) => m.category !== current.category && m.slug !== current.slug
  )
  return [...sameCategory, ...other].slice(0, limit)
}

/**
 * Reference list of valid catalog PDFs (used by tooling/checks).
 */
export const catalogPdfs = {
  general: CATALOG_GENERAL_IT,
  doubleColumn: CATALOG_DOUBLE_COLUMN,
  doubleColumnAlt: CATALOG_DOUBLE_COLUMN_ALT,
  doubleColumnRotary: CATALOG_DOUBLE_COLUMN_ROTARY,
  rotaryTable: CATALOG_ROTARY_TABLE,
}
